import StrokeShapeStrip from '@/components/StrokeShapeStrip';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Brush, Legend,
} from 'recharts';
import { ArrowLeft, Sparkles, RefreshCcw, Activity, Clock, MapPin, Flame, Zap, GitCompare, Star } from 'lucide-react';
import { toast } from 'sonner';
import TopNav from '@/components/TopNav';
import SessionMap from '@/components/SessionMap';
import StrokeVisualizer from '@/components/StrokeVisualizer';
import WorkoutStrokesReplay from '@/components/WorkoutStrokesReplay';
import RaceSplitsCard from '@/components/RaceSplitsCard';
import BenchmarkTrend from '@/components/BenchmarkTrend';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtTime = (s) => {
  if (!s || s <= 0) return '0:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  return h > 0
    ? `${h}:${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
    : `${m}:${sec.toString().padStart(2, '0')}`;
};

const fmtSplit = (seconds) => {
  if (!seconds || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const t = Math.floor((seconds * 10) % 10);
  return `${m}:${s.toString().padStart(2, '0')}.${t}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const RANGE_PRESETS = [
  { id: 'all', label: 'Full Session' },
  { id: 'first30', label: 'First 30s' },
  { id: 'last30', label: 'Last 30s' },
  { id: 'first1k', label: 'First 1000m' },
  { id: 'last1k', label: 'Last 1000m' },
];

// Build a time-aligned chart series from gps_data + stroke_data.
// Time = seconds since start_time. Speed = m/s. Stroke rate from stroke_data.
function buildChartSeries(workout) {
  const startMs = new Date(workout.start_time).getTime();
  const gps = (workout.gps_data || []).map((p) => ({
    t: Math.max(0, (new Date(p.timestamp || startMs).getTime() - startMs) / 1000),
    speed: typeof p.speed === 'number' ? p.speed : null,
  }));
  const stroke = (workout.stroke_data || []).map((s) => ({
    t: Math.max(0, (new Date(s.timestamp || startMs).getTime() - startMs) / 1000),
    spm: typeof s.strokeRate === 'number' ? s.strokeRate : null,
  }));

  // Merge into 1s buckets so X-axis is monotonic.
  const buckets = new Map();
  for (const p of gps) {
    const k = Math.round(p.t);
    if (!buckets.has(k)) buckets.set(k, { t: k });
    if (p.speed != null) buckets.get(k).speed = p.speed;
  }
  for (const s of stroke) {
    const k = Math.round(s.t);
    if (!buckets.has(k)) buckets.set(k, { t: k });
    if (s.spm != null) buckets.get(k).spm = s.spm;
  }
  return [...buckets.values()].sort((a, b) => a.t - b.t);
}

function MetricTile({ icon: Icon, label, value, sub, testId }) {
  return (
    <div
      className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-4"
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {label}
      </div>
      <div
        className="text-2xl font-bold text-zinc-900 dark:text-white"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
      >
        {value}
      </div>
      {sub && <div className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{sub}</div>}
    </div>
  );
}

function CoachRecapPanel({ workoutId, initial }) {
  const [text, setText] = useState(initial || '');
  const [loading, setLoading] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);

  const fetchRecap = useCallback(async (regenerate = false) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API}/workouts/${workoutId}/recap${regenerate ? '?regenerate=true' : ''}`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setText(data.recap || '');
      setGeneratedAt(data.generated_at);
      if (regenerate) toast.success('New recap generated');
    } catch (e) {
      console.error('Recap error:', e);
      toast.error('Coach recap failed — try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [workoutId]);

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 via-white to-emerald-50 dark:from-emerald-950/40 dark:via-zinc-900 dark:to-emerald-950/30 border-2 border-emerald-200 dark:border-emerald-900 rounded-2xl p-5"
      data-testid="coach-recap-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="font-bold text-zinc-900 dark:text-white">Coach&apos;s Recap</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              AI insight from this session&apos;s data
            </div>
          </div>
        </div>
        {text && (
          <button
            type="button"
            onClick={() => fetchRecap(true)}
            disabled={loading}
            className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 hover:text-emerald-900 disabled:opacity-50"
            data-testid="coach-recap-regenerate"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        )}
      </div>

      {text ? (
        <p
          className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap"
          data-testid="coach-recap-text"
        >
          {text}
        </p>
      ) : (
        <button
          type="button"
          onClick={() => fetchRecap(false)}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="coach-recap-generate-btn"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              Get Coach&apos;s Recap
            </>
          )}
        </button>
      )}

      {generatedAt && (
        <div className="text-xs text-zinc-400 mt-3">
          Generated {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

const fmtDelta = (curr, prior, unit = '', invert = false) => {
  const c = Number(curr || 0);
  const p = Number(prior || 0);
  if (!c && !p) return { sign: '—', cls: 'text-zinc-400' };
  const diff = c - p;
  const better = invert ? diff < 0 : diff > 0;
  if (Math.abs(diff) < 1e-9) return { sign: `±0${unit}`, cls: 'text-zinc-500' };
  const arrow = diff > 0 ? '▲' : '▼';
  return {
    sign: `${arrow} ${Math.abs(diff).toFixed(unit === ' W' || unit === ' bpm' || unit === ' spm' || unit === ' m' || unit === ' s' ? 0 : 1)}${unit}`,
    cls: better ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400',
  };
};

const fmtSplitOrDash = (s) => (s && s > 0 ? fmtSplit(s) : '—');

function StatPair({ label, current, prior, unit = '', formatter, invertDelta }) {
  const c = formatter ? formatter(current) : `${current}${unit}`;
  const p = formatter ? formatter(prior) : `${prior}${unit}`;
  const delta = formatter
    ? null
    : fmtDelta(current, prior, unit, invertDelta);
  return (
    <div className="grid grid-cols-3 items-center px-3 py-2 border-b border-zinc-100 dark:border-zinc-800 last:border-b-0">
      <div className="text-xs font-bold uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-mono text-right text-zinc-700 dark:text-zinc-200">{c}</div>
      <div className="text-sm font-mono text-right">
        <span className="text-zinc-500 dark:text-zinc-400 mr-2">{p}</span>
        {delta && <span className={`text-xs font-bold ${delta.cls}`}>{delta.sign}</span>}
      </div>
    </div>
  );
}

function ComparisonPanel({ workoutId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const fetchCompare = useCallback(async (regenerate = false) => {
    setLoading(true);
    setAttempted(true);
    try {
      const res = await fetch(
        `${API}/workouts/${workoutId}/compare${regenerate ? '?regenerate=true' : ''}`,
        { method: 'POST', credentials: 'include' }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const d = await res.json();
      setData(d);
      if (regenerate && d.available) toast.success('Comparison refreshed');
    } catch (e) {
      console.error('Compare error:', e);
      toast.error('Comparison failed — try again in a moment.');
    } finally {
      setLoading(false);
    }
  }, [workoutId]);

  const promoteToBenchmark = useCallback(async () => {
    setPromoting(true);
    try {
      const res = await fetch(`${API}/workouts/${workoutId}/benchmark`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success('New benchmark set — your bar just moved up.');
      // Re-run the comparison so the panel now shows the OLD benchmark in the prior slot
      // (which after promotion is no longer the benchmark, but the comparison cache is stale).
      await fetchCompare(true);
    } catch (e) {
      console.error('Promote benchmark error:', e);
      toast.error('Could not set as benchmark');
    } finally {
      setPromoting(false);
    }
  }, [workoutId, fetchCompare]);

  if (!attempted) {
    return (
      <div
        className="bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-2xl p-5"
        data-testid="compare-panel"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 rounded-full bg-amber-500 text-white flex items-center justify-center">
            <Star className="w-4 h-4 fill-current" />
          </div>
          <div>
            <div className="font-bold text-zinc-900 dark:text-white">Compare with Benchmark</div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              Side-by-side stats + AI delta read against your benchmark session for this boat.
              <span className="block mt-0.5 text-zinc-400 dark:text-zinc-500">
                No benchmark set yet? We&apos;ll fall back to your most recent prior session.
              </span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchCompare(false)}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2"
          data-testid="compare-generate-btn"
        >
          {loading ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Comparing…
            </>
          ) : (
            <>
              <Star className="w-4 h-4 fill-current" />
              Compare with Benchmark
            </>
          )}
        </button>
      </div>
    );
  }

  if (!data || data.available === false) {
    return (
      <div
        className="bg-zinc-50 dark:bg-zinc-800/50 border-2 border-dashed border-zinc-200 dark:border-zinc-700 rounded-2xl p-5 text-sm text-zinc-500"
        data-testid="compare-panel-empty"
      >
        {data?.message || 'No prior session yet — finish another row to unlock comparison.'}
      </div>
    );
  }

  const { current, prior, comparison, generated_at: generatedAt, current_beats_prior: currentBeatsPrior } = data;
  const priorDate = prior?.start_time
    ? new Date(prior.start_time).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
  const priorIsBenchmark = !!prior?.is_benchmark;

  return (
    <div
      className="bg-gradient-to-br from-violet-50 via-white to-violet-50 dark:from-violet-950/40 dark:via-zinc-900 dark:to-violet-950/30 border-2 border-violet-200 dark:border-violet-900 rounded-2xl p-5"
      data-testid="compare-panel"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-full ${priorIsBenchmark ? 'bg-amber-500' : 'bg-violet-500'} text-white flex items-center justify-center`}>
            {priorIsBenchmark ? <Star className="w-4 h-4 fill-current" /> : <GitCompare className="w-4 h-4" />}
          </div>
          <div>
            <div className="font-bold text-zinc-900 dark:text-white flex items-center gap-2">
              {priorIsBenchmark ? 'vs Benchmark' : 'vs Last Session'}
              {priorIsBenchmark && (
                <span
                  className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 text-[10px] font-bold rounded-full uppercase tracking-wider"
                  data-testid="compare-benchmark-badge"
                >
                  Benchmark
                </span>
              )}
            </div>
            <div className="text-xs text-zinc-500 dark:text-zinc-400">
              {prior.session_name || prior.boat_type} on {priorDate}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => fetchCompare(true)}
          disabled={loading}
          className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300 hover:text-violet-900 disabled:opacity-50"
          data-testid="compare-regenerate"
        >
          <RefreshCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Regenerate
        </button>
      </div>

      <p
        className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-200 whitespace-pre-wrap mb-4"
        data-testid="compare-text"
      >
        {comparison}
      </p>

      {/* Promote-to-benchmark CTA — only when the current session beats the prior */}
      {currentBeatsPrior && !current?.is_benchmark && (
        <button
          type="button"
          onClick={promoteToBenchmark}
          disabled={promoting}
          data-testid="promote-benchmark-btn"
          className="w-full mb-4 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-colors"
        >
          {promoting ? (
            <>
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              Setting…
            </>
          ) : (
            <>
              <Star className="w-4 h-4 fill-current" />
              {priorIsBenchmark ? 'Make this your new Benchmark' : 'Set this session as Benchmark'}
            </>
          )}
        </button>
      )}

      {current?.is_benchmark && (
        <div
          className="mb-4 px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 text-xs font-bold uppercase tracking-wider flex items-center gap-2"
          data-testid="compare-current-is-benchmark"
        >
          <Star className="w-3.5 h-3.5 fill-current" />
          This session is your current Benchmark
        </div>
      )}

      <div
        className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden"
        data-testid="compare-stats"
      >
        <div className="grid grid-cols-3 px-3 py-2 border-b border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 text-xs font-bold uppercase tracking-wider text-zinc-500">
          <div>Metric</div>
          <div className="text-right">Current</div>
          <div className="text-right">Prior · Δ</div>
        </div>
        <StatPair
          label="Duration"
          current={current.duration} prior={prior.duration}
          formatter={fmtTime}
        />
        <StatPair label="Distance" current={Math.round(current.distance)} prior={Math.round(prior.distance)} unit=" m" />
        <StatPair
          label="Avg split"
          current={current.avg_split} prior={prior.avg_split}
          formatter={fmtSplitOrDash}
        />
        <StatPair label="Stroke rate" current={current.avg_stroke_rate} prior={prior.avg_stroke_rate} unit=" spm" />
        <StatPair label="Avg HR" current={current.avg_heart_rate} prior={prior.avg_heart_rate} unit=" bpm" invertDelta />
        <StatPair label="Max HR" current={current.max_heart_rate} prior={prior.max_heart_rate} unit=" bpm" invertDelta />
        <StatPair label="Avg power" current={current.avg_watts} prior={prior.avg_watts} unit=" W" />
      </div>

      {generatedAt && (
        <div className="text-xs text-zinc-400 mt-3">
          Generated {new Date(generatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}

function StrokeDetailsTab({ workout }) {
  const strokeArrays = workout.stroke_arrays || [];
  const strokeData = workout.stroke_data || [];

  // Per-stroke aggregated stats (max force, peak power, avg angle range).
  const perStrokeStats = useMemo(() => {
    return strokeArrays.map((s) => {
      const forces = s.forceN || [];
      const angles = s.angleDeg || [];
      const peakForce = forces.length ? Math.max(...forces) : 0;
      const minAng = angles.length ? Math.min(...angles) : 0;
      const maxAng = angles.length ? Math.max(...angles) : 0;
      return {
        i: s.i,
        side: s.side || 'sweep',
        spm: s.spm,
        peakForce: peakForce.toFixed(0),
        catchAngle: minAng.toFixed(1),
        finishAngle: maxAng.toFixed(1),
        length: (maxAng - minAng).toFixed(1),
      };
    });
  }, [strokeArrays]);

  if (!strokeArrays.length && !strokeData.length) {
    return (
      <div
        className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-6 text-center text-sm text-zinc-500"
        data-testid="stroke-details-empty"
      >
        No per-stroke arrays were captured for this session. Connect a CREWZ or NK EmPower
        smart-gate next time to populate this view.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stroke-details-tab">
      {strokeArrays.length > 0 && (
        <>
          {/* Per-stroke metrics table */}
          <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-200 dark:border-zinc-700">
              <div className="font-bold text-zinc-900 dark:text-white">Metrics per Stroke</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                {strokeArrays.length} captured
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stroke-details-table">
                <thead className="bg-zinc-50 dark:bg-zinc-900/50 text-xs uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Stroke #</th>
                    <th className="px-3 py-2 text-left">Side</th>
                    <th className="px-3 py-2 text-right">Peak Force (N)</th>
                    <th className="px-3 py-2 text-right">Catch (°)</th>
                    <th className="px-3 py-2 text-right">Finish (°)</th>
                    <th className="px-3 py-2 text-right">Length (°)</th>
                    <th className="px-3 py-2 text-right">SPM</th>
                  </tr>
                </thead>
                <tbody>
                  {perStrokeStats.slice(0, 60).map((s) => (
                    <tr
                      key={s.i}
                      className="border-t border-zinc-100 dark:border-zinc-800 text-zinc-700 dark:text-zinc-200"
                    >
                      <td className="px-3 py-2 font-mono">{s.i}</td>
                      <td className="px-3 py-2 capitalize">{s.side}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.peakForce}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.catchAngle}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.finishAngle}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.length}</td>
                      <td className="px-3 py-2 text-right font-mono">{s.spm ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {strokeArrays.length > 60 && (
              <div className="px-4 py-2 text-xs text-zinc-500 dark:text-zinc-400 border-t border-zinc-100 dark:border-zinc-800">
                Showing first 60 of {strokeArrays.length} strokes. Use Stroke Replay below to scrub
                through all of them.
              </div>
            )}
          </div>

          {/* Stroke Profile (scrubbable replay using the existing visualizer) */}
          <WorkoutStrokesReplay workout={workout} />

          {/* Demo / latest stroke profile chart */}
          <StrokeVisualizer hardwareConnected={false} />
        </>
      )}
    </div>
  );
}

function SummaryTab({ workout }) {
  const distance = workout.distance || 0;
  const duration = workout.duration || 0;
  const strokes = workout.total_strokes ?? (workout.stroke_data || []).length;
  const distPerStroke = strokes > 0 ? (distance / strokes).toFixed(2) : '—';
  const noHrStrap = !workout.is_pm5_session && workout.app_mode !== 3 && !workout.calories && !(workout.hr_bpm_series || []).length;

  return (
    <div className="space-y-6" data-testid="summary-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        <MetricTile icon={Clock} label="Work Time" value={fmtTime(duration)} testId="summary-duration" />
        <MetricTile icon={MapPin} label="Distance" value={`${Math.round(distance)} m`} sub={`${(distance / 1000).toFixed(2)} km`} testId="summary-distance" />
        <MetricTile icon={Activity} label="Stroke Rate" value={workout.avg_stroke_rate ? `${workout.avg_stroke_rate}` : '--'} sub="avg spm" testId="summary-strokerate" />
        <MetricTile icon={Activity} label="Pace" value={fmtSplit(workout.avg_split)} sub="avg / 500m" testId="summary-pace" />
        <MetricTile icon={Activity} label="Strokes" value={strokes > 0 ? strokes : '—'} testId="summary-strokes" />
        <MetricTile icon={MapPin} label="Dist/Stroke" value={`${distPerStroke}`} sub="m / stroke" testId="summary-distperstroke" />
        <MetricTile icon={Flame} label="Calories" value={noHrStrap ? '--' : (workout.calories || 0)} sub={noHrStrap ? 'Needs heart-rate strap' : 'kcal'} testId="summary-calories" />
        <MetricTile icon={Zap} label="Avg Power" value={workout.avg_watts ? `${workout.avg_watts} W` : '--'} testId="summary-power" />
        <MetricTile icon={Activity} label="Avg HR" value={workout.avg_heart_rate ? `${workout.avg_heart_rate}` : '--'} sub="bpm · moving" testId="summary-avghr" />
        <MetricTile icon={Activity} label="Max HR" value={workout.max_hr_bpm ? `${workout.max_hr_bpm}` : '--'} sub="bpm · held 5 s" testId="summary-maxhr" />
      </div>

      {(workout.splits_500m || []).length > 0 && (
        <RaceSplitsCard
          splits={workout.splits_500m}
          raceDistance={workout.target_distance || workout.distance}
          variant="inline"
          context={{
            date: workout.start_time,
            isErgo: !!workout.is_pm5_session,
            boatClass: workout.boat_type,
            savedToHistory: true,
          }}
        />
      )}
    </div>
  );
}

export function SessionAnalysisInline({ workout }) {
  const [activeTab, setActiveTab] = useState('summary');
  const [rangeId, setRangeId] = useState('all');
  const [brushIndex, setBrushIndex] = useState(0);

  const allSeries = useMemo(
    () => (workout ? buildChartSeries(workout) : []),
    [workout]
  );

  const series = useMemo(() => {
    if (!allSeries.length) return [];
    const lastT = allSeries[allSeries.length - 1].t;
    switch (rangeId) {
      case 'first30':
        return allSeries.filter((p) => p.t <= 30);
      case 'last30':
        return allSeries.filter((p) => p.t >= lastT - 30);
      case 'first1k': {
        let dist = 0;
        const out = [];
        let prevT = 0;
        for (const p of allSeries) {
          if (p.speed) dist += p.speed * (p.t - prevT);
          prevT = p.t;
          out.push(p);
          if (dist >= 1000) break;
        }
        return out;
      }
      case 'last1k': {
        let dist = 0;
        const out = [];
        for (let i = allSeries.length - 1; i >= 0; i--) {
          const p = allSeries[i];
          const next = allSeries[i + 1];
          if (next && p.speed) dist += p.speed * (next.t - p.t);
          out.unshift(p);
          if (dist >= 1000) break;
        }
        return out;
      }
      default:
        return allSeries;
    }
  }, [allSeries, rangeId]);

  if (!workout) return null;

  return (
    <div className="space-y-6" data-testid="session-analysis-inline">
      {/* Tabs */}
      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden">
        <div className="flex border-b border-zinc-200 dark:border-zinc-700">
          {[
            { id: 'summary', label: 'Summary' },
            { id: 'stroke', label: 'Stroke Details' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 text-sm font-bold uppercase tracking-wider transition-colors ${
                activeTab === tab.id
                  ? 'text-emerald-700 dark:text-emerald-300 border-b-2 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30'
                  : 'text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-200'
              }`}
              data-testid={`analysis-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="p-4">
          {activeTab === 'summary' ? <SummaryTab workout={workout} /> : <StrokeDetailsTab workout={workout} />}
        </div>
      </div>

      {/* GPS map */}
      <SessionMap gpsData={workout.gps_data || []} scrubIndex={brushIndex} />

      {/* Chart: Speed (m/s) + Stroke Rate over time */}
      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-4 text-xs font-bold uppercase tracking-wider">
            <span className="flex items-center gap-1.5 text-emerald-600">
              <span className="w-3 h-3 rounded-sm bg-emerald-500"></span>
              Speed (m/s)
            </span>
            <span className="flex items-center gap-1.5 text-violet-600">
              <span className="w-3 h-3 rounded-sm bg-violet-500"></span>
              Stroke Rate
            </span>
          </div>
          <select
            value={rangeId}
            onChange={(e) => setRangeId(e.target.value)}
            className="border border-zinc-300 dark:border-zinc-600 rounded-lg px-3 py-1.5 text-sm bg-white dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200"
            data-testid="analysis-range-select"
          >
            {RANGE_PRESETS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {series.length === 0 ? (
          <div className="h-56 flex items-center justify-center text-sm text-zinc-500" data-testid="analysis-chart-empty">
            No chart data captured for this session.
          </div>
        ) : (
          <div className="h-56 sm:h-72" data-testid="analysis-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                <XAxis dataKey="t" tickFormatter={(t) => fmtTime(t)} stroke="#71717a" fontSize={11} />
                <YAxis yAxisId="speed" orientation="left" stroke="#10b981" fontSize={11} unit=" m/s" />
                <YAxis yAxisId="spm" orientation="right" stroke="#8b5cf6" fontSize={11} unit=" spm" domain={[0, 50]} allowDataOverflow />
                <Tooltip
                  formatter={(v, name) =>
                    name === 'speed' ? [`${(v ?? 0).toFixed(2)} m/s`, 'Speed'] : [`${v} spm`, 'Stroke Rate']
                  }
                  labelFormatter={(t) => `t = ${fmtTime(t)}`}
                />
                <Legend wrapperStyle={{ display: 'none' }} />
                <Line yAxisId="speed" type="monotone" dataKey="speed" name="speed" stroke="#10b981" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                <Line yAxisId="spm" type="monotone" dataKey="spm" name="spm" stroke="#8b5cf6" strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                <Brush
                  dataKey="t"
                  height={22}
                  stroke="#71717a"
                  tickFormatter={(t) => fmtTime(t)}
                  onChange={(range) => {
                    if (range && typeof range.startIndex === 'number') {
                      setBrushIndex(range.startIndex);
                    }
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Stroke Shape: every-500m curve snapshots */}
      <StrokeShapeStrip workout={workout} />

      {/* Coach's Recap */}
      <CoachRecapPanel workoutId={workout.workout_id} initial={workout.coach_recap} />

      {/* Compare with Benchmark */}
      <ComparisonPanel workoutId={workout.workout_id} />

      {/* Distance-from-Benchmark sparkline */}
      <BenchmarkTrend boatType={workout.boat_type} currentWorkoutId={workout.workout_id} />
    </div>
  );
}

export default function TrainingSessionAnalysis() {
  const { workoutId } = useParams();
  const navigate = useNavigate();
  const [workout, setWorkout] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/workouts/${workoutId}`, { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setWorkout(data);
      } catch (e) {
        console.error('Load workout error:', e);
        toast.error('Could not load this workout');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [workoutId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
        <TopNav />
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-sm text-zinc-500">
          Loading session…
        </div>
      </div>
    );
  }

  if (!workout) {
    return (
      <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900">
        <TopNav />
        <div className="max-w-5xl mx-auto px-4 py-12 text-center text-sm text-zinc-500">
          Workout not found.{' '}
          <button onClick={() => navigate('/history')} className="underline">
            Back to history
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-900" data-testid="training-session-analysis">
      <TopNav />
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="flex items-start gap-3">
          <button
            onClick={() => navigate('/history')}
            className="p-2 -ml-2 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800"
            data-testid="analysis-back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-700 dark:text-zinc-200" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white" data-testid="analysis-title">
              {workout.session_name || 'Training Session Analysis'}
            </h1>
            <div className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Start {fmtDate(workout.start_time)} · End {fmtDate(workout.end_time)}
              {' · '}
              {fmtTime(workout.duration)}
            </div>
          </div>
        </div>

        <SessionAnalysisInline workout={workout} />
      </div>
    </div>
  );
}
